import type {OrderFilter, OrderFiltersValue} from '../../types';

import {
  AppstoreOutlined,
  CarOutlined,
  FileDoneOutlined,
  GiftOutlined,
  SortAscendingOutlined
} from '@ant-design/icons';
import {convertJalaliToGregorian, DatePicker} from '@date-picker';
import {BottomSheet, Button, Checkbox, Chip, Radio} from '@ds/components';
import {useState} from 'react';

const FILTER_ICONS: Record<string, React.ReactNode> = {
  date: <SortAscendingOutlined />,
  is_modified: <AppstoreOutlined />,
  coupon: <GiftOutlined />,
  expedition_type: <CarOutlined />,
  order_status: <FileDoneOutlined />
};

type OrdersFiltersProps = {
  filters: OrderFilter[];
  values: OrderFiltersValue;
  onApply: (filter: OrderFilter, filters?: OrderFilter[]) => boolean;
  onClear: (filter: OrderFilter) => void;
};

const OrdersFilters: React.FC<OrdersFiltersProps> = ({filters, values, onApply, onClear}) => {
  const [draftFilter, setDraftFilter] = useState<OrderFilter | null>(null);

  const closeBottomSheet = () => {
    setDraftFilter(null);
  };

  const updateDraftFilter = (updater: (filter: OrderFilter) => OrderFilter) => {
    setDraftFilter((currentFilter) => (currentFilter ? updater(currentFilter) : currentFilter));
  };

  const toggleFilter = (filter: OrderFilter) => {
    if (filter.type === 'selectable') {
      const nextFilters = filters.map((item) =>
        item.name === filter.name ? {...item, selected: !item.selected} : item
      );
      onApply(nextFilters.find((item) => item.name === filter.name)!, nextFilters);
      return;
    }
    setDraftFilter({
      ...filter,
      items: filter.items?.map((item) => ({
        ...item,
        date: item.date ? {...item.date} : undefined
      }))
    });
  };

  const toggleItem = (itemValue: string) => {
    updateDraftFilter((filter) => ({
      ...filter,
      items: filter.items?.map((item) => ({
        ...item,
        selected:
          filter.type === 'multiSelectable'
            ? item.value === itemValue
              ? !item.selected
              : item.selected
            : item.value === itemValue
      }))
    }));
  };

  const setCustomDate = (side: 'start_date' | 'end_date', date: string) => {
    updateDraftFilter((filter) => ({
      ...filter,
      items: filter.items?.map((item) =>
        item.key === 'customDate' && item.date
          ? {...item, date: {...item.date, [side]: date}}
          : item
      )
    }));
  };

  const hasAppliedValue = draftFilter
    ? draftFilter.name === 'is_modified'
      ? values.is_modified
      : draftFilter.name === 'date'
        ? filters
            .find((filter) => filter.name === draftFilter.name)
            ?.items?.some((item) => item.selected)
        : values[draftFilter.name as 'coupon' | 'expedition_type' | 'order_status']?.length
    : false;

  const footer = draftFilter ? (
    <div className="flex flex-col gap-2.5 border-t border-gray-03 p-2.5">
      <Button
        fullWidth
        disabled={!draftFilter.items?.some((item) => item.selected)}
        onClick={() => {
          const nextFilters = filters.map((filter) =>
            filter.name === draftFilter.name ? draftFilter : filter
          );
          if (onApply(draftFilter, nextFilters)) {
            closeBottomSheet();
          }
        }}
      >
        تایید فیلتر
      </Button>
      <Button
        fullWidth
        variant="ghost"
        disabled={!hasAppliedValue}
        onClick={() => {
          onClear(draftFilter);
          closeBottomSheet();
        }}
      >
        حذف فیلتر
      </Button>
    </div>
  ) : null;

  return (
    <>
      <div className="min-h-12.5 overflow-x-auto p-2.5">
        <div className="flex w-max gap-1">
          {filters.map((filter) => {
            const selected =
              filter.selected || filter.items?.some((item) => item.selected) || false;
            return (
              <Chip
                key={filter.name}
                label={filter.title}
                icon={FILTER_ICONS[filter.name]}
                selected={selected}
                onClick={() => toggleFilter(filter)}
              />
            );
          })}
        </div>
      </div>

      <BottomSheet
        show={Boolean(draftFilter)}
        defaultHeader
        title={draftFilter?.title}
        footer={footer}
        onClose={closeBottomSheet}
      >
        {draftFilter?.type === 'multiSelectable' && (
          <div className="flex min-h-25 flex-col gap-5 p-4">
            {draftFilter.items?.map((item) => (
              <Checkbox
                key={item.key}
                checked={item.selected}
                onChange={() => toggleItem(item.value)}
              >
                {item.title}
              </Checkbox>
            ))}
          </div>
        )}

        {draftFilter?.type === 'custom' && (
          <div className="flex min-h-25 flex-col gap-5 p-4">
            <Radio.Group
              className="gap-5"
              value={draftFilter.items?.find((item) => item.selected)?.value ?? ''}
              onChange={(event) => toggleItem(event.target.value)}
            >
              {draftFilter.items?.map((item) => (
                <Radio key={item.key} value={item.value}>
                  {item.title}
                </Radio>
              ))}
            </Radio.Group>
            {draftFilter.items?.some((item) => item.key === 'customDate' && item.selected) && (
              <div className="grid grid-cols-2 gap-2.5">
                <DatePicker
                  label="از تاریخ"
                  onChange={(_, date) =>
                    setCustomDate('start_date', date ? convertJalaliToGregorian(date) : '')
                  }
                />
                <DatePicker
                  label="تا تاریخ"
                  onChange={(_, date) =>
                    setCustomDate('end_date', date ? convertJalaliToGregorian(date) : '')
                  }
                />
              </div>
            )}
          </div>
        )}
      </BottomSheet>
    </>
  );
};

export default OrdersFilters;
